import React, { useState, useRef } from "react";
import { HiUpload, HiTrash, HiExternalLink } from "react-icons/hi";
import { Button } from "../ui/Button";

interface VideoManagementSectionProps {
  videoUrl: string | null;
  onUpload?: (file: File) => Promise<void>;
  onDelete?: () => Promise<void>;
}

/**
 * VideoManagementSection 컴포넌트
 * 
 * 레슨 영상을 관리하는 섹션
 * - 영상 미리보기 플레이어
 * - 영상 메타데이터 표시
 * - 영상 교체/삭제 기능
 */
export const VideoManagementSection: React.FC<VideoManagementSectionProps> = ({
  videoUrl,
  onUpload: _onUpload,
  onDelete,
}) => {
  const [videoMetadata, setVideoMetadata] = useState<{
    duration: string;
    width: number;
    height: number;
  } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  /**
   * 비디오 메타데이터 로드 핸들러
   */
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      const duration = formatDuration(video.duration);
      setVideoMetadata({
        duration,
        width: video.videoWidth,
        height: video.videoHeight,
      });
    }
  };

  /**
   * 시간 포맷팅 (초 -> MM:SS)
   */
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  /**
   * 파일명 추출
   */
  const getFileName = (url: string): string => {
    return url.split("/").pop() || "video.mp4";
  };

  /**
   * 해상도 레이블
   */
  const getResolutionLabel = (width: number, height: number): string => {
    if (height >= 2160) return "4K";
    if (height >= 1080) return "1080p";
    if (height >= 720) return "720p";
    if (height >= 480) return "480p";
    return `${width}x${height}`;
  };

  if (!videoUrl) {
    return (
      <div className="bg-slate-50 rounded-lg p-8 border-2 border-dashed border-slate-300 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
            <HiUpload className="w-8 h-8 text-slate-400" />
          </div>
          <div>
            <h4 className="text-lg font-semibold text-slate-700 mb-1">
              업로드된 영상이 없습니다
            </h4>
            <p className="text-sm text-slate-500">
              레슨 수정 페이지에서 영상을 업로드할 수 있습니다
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 비디오 플레이어 */}
      <div className="bg-black rounded-lg overflow-hidden shadow-lg">
        <video
          ref={videoRef}
          controls
          src={videoUrl}
          className="w-full"
          onLoadedMetadata={handleLoadedMetadata}
        >
          브라우저가 동영상 재생을 지원하지 않습니다.
        </video>
      </div>

      {/* 메타데이터 바 */}
      {videoMetadata && (
        <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center gap-2 text-sm">
            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-medium">
              {getResolutionLabel(videoMetadata.width, videoMetadata.height)}
            </span>
            <span className="text-slate-600">•</span>
            <span className="text-slate-700 font-medium">
              {videoMetadata.duration}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-600 truncate">
              {getFileName(videoUrl)}
            </p>
          </div>
        </div>
      )}

      {/* 컨트롤 바 */}
      <div className="flex items-center gap-3">
        <a
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <HiExternalLink className="w-4 h-4" />
          새 창에서 보기
        </a>
        <a
          href={videoUrl}
          download
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          다운로드
        </a>
        {onDelete && (
          <Button
            variant="danger"
            size="sm"
            onClick={onDelete}
            leftIcon={<HiTrash />}
          >
            영상 삭제
          </Button>
        )}
      </div>

      {/* URL 정보 */}
      <details className="group">
        <summary className="cursor-pointer text-sm text-slate-600 hover:text-slate-800 select-none">
          URL 정보 보기
        </summary>
        <div className="mt-2 p-3 bg-slate-50 rounded border border-slate-200">
          <p className="text-xs text-slate-600 break-all font-mono">
            {videoUrl}
          </p>
        </div>
      </details>
    </div>
  );
};

export default VideoManagementSection;
